import { apiRequest } from '../../shared/api/client';

/**
 * Transaction codes — the master every posting is stamped with, mirroring the current product's
 * `tblWebTranCode` and its maintenance screen.
 *
 *   GET    /api/v1/transaction-codes[?groupCode=Adj&includeInactive=true]   any signed-in user
 *   GET    /api/v1/transaction-codes/{id}                                   any signed-in user
 *   GET    /api/v1/transaction-code-types                                   any signed-in user
 *   GET    /api/v1/transaction-code-groups                                  any signed-in user
 *   POST   /api/v1/transaction-codes                                        tenant:manage_settings
 *   PUT    /api/v1/transaction-codes/{id}                                   tenant:manage_settings
 *   DELETE /api/v1/transaction-codes/{id}                                   tenant:manage_settings
 *
 * Reading is open to any signed-in user because every posting screen has to offer the codes; only
 * changing them is configuration.
 *
 * There is no QuickBooks account here. The current product keeps one on the code itself, but the
 * integration is a paid add-on in this platform, so that mapping belongs to the add-on.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

/** Legacy's width. A longer code cannot be stored, and the API refuses rather than truncating. */
export const TRAN_CODE_MAX_LENGTH = 3;

/** The group the adjustment screen draws from, as legacy's `TransactionCodeGetForGroup('adj')` does. */
export const ADJUSTMENT_GROUP_CODE = 'Adj';

export interface TransactionCodeDto {
  transactionCodeId: string;
  tranCode: string;
  displayName: string;
  transactionCodeTypeId: number;
  /** INCREMENT | DECREMENT | MOVE | MOVEREV */
  typeCode: string;
  typeName: string;
  /** +1, -1 or 0. Move and MoveRev both net to zero; they differ in meaning, not effect. */
  sign: number;
  transactionCodeGroupId: string;
  groupCode: string;
  groupName: string;
  /** A code the platform posts with: its code, effect and group are fixed and it cannot be removed. */
  isLocked: boolean;
  isActive: boolean;
  /** Something has posted with it, so it can never be deleted — only deactivated. */
  isInUse: boolean;
}

export interface TransactionCodeTypeDto {
  transactionCodeTypeId: number;
  typeCode: string;
  displayName: string;
  sign: number;
}

export interface TransactionCodeGroupDto {
  transactionCodeGroupId: string;
  groupCode: string;
  displayName: string;
  isActive: boolean;
  codeCount: number;
}

export interface TransactionCodeRequest {
  tranCode: string;
  displayName: string;
  transactionCodeTypeId: number;
  transactionCodeGroupId: string;
  isActive: boolean;
}

export async function listTransactionCodes(
  options: { groupCode?: string; includeInactive?: boolean } = {},
  signal?: AbortSignal,
): Promise<TransactionCodeDto[]> {
  const params = new URLSearchParams();
  if (options.groupCode) params.set('groupCode', options.groupCode);
  if (options.includeInactive) params.set('includeInactive', 'true');
  const query = params.toString();

  const { data } = await apiRequest<TransactionCodeDto[]>(
    `/v1/transaction-codes${query ? `?${query}` : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function listTransactionCodeTypes(
  signal?: AbortSignal,
): Promise<TransactionCodeTypeDto[]> {
  const { data } = await apiRequest<TransactionCodeTypeDto[]>(
    '/v1/transaction-code-types',
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function listTransactionCodeGroups(
  signal?: AbortSignal,
): Promise<TransactionCodeGroupDto[]> {
  const { data } = await apiRequest<TransactionCodeGroupDto[]>(
    '/v1/transaction-code-groups',
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function createTransactionCode(
  body: TransactionCodeRequest,
): Promise<TransactionCodeDto> {
  const { data } = await apiRequest<TransactionCodeDto>('/v1/transaction-codes', {
    method: 'POST',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateTransactionCode(
  id: string,
  body: TransactionCodeRequest,
): Promise<TransactionCodeDto> {
  const { data } = await apiRequest<TransactionCodeDto>(
    `/v1/transaction-codes/${encodeURIComponent(id)}`,
    { method: 'PUT', body, ...COOKIE_INIT },
  );
  return data;
}

export async function deleteTransactionCode(id: string): Promise<void> {
  await apiRequest<void>(`/v1/transaction-codes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...COOKIE_INIT,
  });
}

/** How a code reads in a picker: `MP — Misc Positive Adjustment`. */
export function transactionCodeLabel(code: TransactionCodeDto): string {
  return `${code.tranCode} — ${code.displayName}`;
}
